import React, { useState } from "react";
import { useParams, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import TripDetails from "./TripDetails";
import VariantDetails from "./VariantDetails";
import { TripProvider, useTripContext } from "./TripContext";
import LoadingOverlay from "../../LoadingOverlay";
import VariantLayout from "./Variants/VariantLayout";
import "../../css/TripWrapper.css";
import RegistrationEmailHistory from "./Registrations/RegistrationEmailHistory";
import RequestFullDataModal from "./RequestFullDataModal";

const sectionLabels = {
  registrations: "Rejestracje",
  participants: "Uczestnix",
  discord: "Discord",
  insurances: "Ubezpieczenia",
  access: "Dostęp",
  summary: "Zestawienia",
};

const InnerRoutes = () => {
  const { tripData, fetchTripData, fetchTripFullData, isFullData, error, loading } = useTripContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFullModalOpen, setIsFullModalOpen] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchTripData();
    setIsRefreshing(false);
  };

  const handleFetchFull = async (reason) => {
    await fetchTripFullData(reason);
    setIsFullModalOpen(false);
  };

  if (!tripData && error) return <div className="trip-details-error"><h2>Nie udało się załadować wycieczki</h2><p>{error.message || "Wystąpił błąd podczas pobierania danych."}</p></div>;
  if (!tripData) return <LoadingOverlay text="Ładowanie szczegółów wycieczki" />;
  if (isRefreshing) return <LoadingOverlay text="Odświeżanie danych" />;

  const pathParts = location.pathname.split("/").filter(Boolean);
  const variantsIndex = pathParts.indexOf("variants");
  const registrationsIndex = pathParts.indexOf("registrations");
  const participantsIndex = pathParts.indexOf("participants");
  const variantGuid = variantsIndex >= 0 ? pathParts[variantsIndex + 1] : null;
  const variantSection = variantsIndex >= 0 ? pathParts[variantsIndex + 2] : null;
  const reservationGuid = registrationsIndex >= 0 ? pathParts[registrationsIndex + 1] : null;
  const participantGuid = participantsIndex >= 0 ? pathParts[participantsIndex + 1] : null;
  const tripSection = variantsIndex < 0 ? pathParts[2] : null;
  const isEditingTrip = new URLSearchParams(location.search).get("edit") === "true";

  const activeVariant = variantGuid ? tripData.variants?.find(v => v.guid === variantGuid) : null;
  const activeRegistration = reservationGuid ? tripData.registrations?.find(r => r.reservationGuid === reservationGuid) : null;
  const activeParticipant = participantGuid
    ? tripData.registrations?.flatMap(r => (r.participants || []).map(p => ({ ...p, registrationGuid: r.reservationGuid, registrationCode: r.reservationCode, variantGuid: r.variantGuid }))).find(p => p.guid === participantGuid)
    : null;
  const participantRegistration = activeParticipant ? tripData.registrations?.find(r => r.reservationGuid === activeParticipant.registrationGuid) : null;
  const participantVariant = activeParticipant ? tripData.variants?.find(v => v.guid === activeParticipant.variantGuid) : activeVariant;
  const breadcrumbRegistration = activeRegistration || participantRegistration;
  const breadcrumbVariant = activeVariant || participantVariant;
  const primaryParticipant = breadcrumbRegistration?.participants?.find(p => p.isPrimary);
  const registrationBreadcrumbPath = breadcrumbRegistration
    ? (variantsIndex >= 0
      ? `/trips/${tripData.guid}/variants/${breadcrumbRegistration.variantGuid}/registrations/${breadcrumbRegistration.reservationGuid}`
      : `/trips/${tripData.guid}/registrations/${breadcrumbRegistration.reservationGuid}`)
    : null;

  const breadcrumbItems = [
    { label: "Wycieczki", onClick: () => navigate("/") },
    { label: tripData.name, onClick: () => navigate(`/trips/${tripData.guid}`) },
  ];

  if (isEditingTrip) {
    breadcrumbItems.push({ label: "Edycja" });
  } else {
    if (breadcrumbVariant) breadcrumbItems.push({ label: breadcrumbVariant.name, onClick: () => navigate(`/trips/${tripData.guid}/variants/${breadcrumbVariant.guid}`) });
    if (breadcrumbRegistration) breadcrumbItems.push({ label: `${breadcrumbRegistration.reservationCode || "Rezerwacja"}${primaryParticipant?.preferredName ? ` (${primaryParticipant.preferredName})` : ""}`, onClick: () => navigate(registrationBreadcrumbPath) });
    if (activeParticipant) breadcrumbItems.push({ label: activeParticipant.preferredName || "Osoba" });
    else if (reservationGuid && location.pathname.endsWith("/emailHistory")) breadcrumbItems.push({ label: "Historia e-maili" });
    else if (!reservationGuid && !participantGuid && breadcrumbVariant && variantSection && sectionLabels[variantSection]) breadcrumbItems.push({ label: sectionLabels[variantSection] });
    else if (!reservationGuid && !participantGuid && tripSection && sectionLabels[tripSection]) {
      breadcrumbItems.push({ label: sectionLabels[tripSection] });
      if (tripSection === "discord" && pathParts[3]) breadcrumbItems.push({ label: "Kanał" });
    }
  }

  return (
    <div className="trip-details-container">
      {!isFullData && (
        <button className="request-fulldata-btn top-right" onClick={() => setIsFullModalOpen(true)} title="Pobierz pełne dane (dostęp logowany)">
          <i className="fa-solid fa-shield-halved" /> Pobierz pełne dane
        </button>
      )}

      <div className="trip-mobile-heading">
        <h1>{tripData.name}</h1>
        <button type="button" onClick={handleRefresh} title="Odśwież dane"><i className="fa-solid fa-arrows-rotate" /></button>
      </div>

      <Routes>
        <Route path="/" element={<TripDetails activeSection="registrations" mobileRoot />} />
        <Route path="registrations" element={<TripDetails activeSection="registrations" />} />
        <Route path="registrations/:resGuid" element={<TripDetails activeSection="registrations" sectionContent="registrationDetails" />} />
        <Route path="participants" element={<TripDetails activeSection="participants" />} />
        <Route path="participants/:participantGuid" element={<TripDetails activeSection="participants" sectionContent="participantDetails" />} />
        <Route path="insurances" element={<TripDetails activeSection="insurances" />} />
        <Route path="access" element={<TripDetails activeSection="access" />} />
        <Route path="summary" element={<TripDetails activeSection="summary" />} />
        <Route path="discord" element={<TripDetails activeSection="discord" />} />
        <Route path="discord/:channelId" element={<TripDetails activeSection="discord" sectionContent="discordDetails" />} />

        <Route path="variants/:variantguid" element={<VariantLayout />}>
          <Route index element={<VariantDetails activeSection="registrations" mobileRoot />} />
          <Route path="registrations">
            <Route index element={<VariantDetails activeSection="registrations" />} />
            <Route path=":resGuid" element={<VariantDetails activeSection="registrations" sectionContent="registrationDetails" />} />
            <Route path=":resGuid/emailHistory" element={<RegistrationEmailHistory />} />
          </Route>
          <Route path="participants" element={<VariantDetails activeSection="participants" />} />
          <Route path="participants/:participantGuid" element={<VariantDetails activeSection="participants" sectionContent="participantDetails" />} />
          <Route path="insurances" element={<VariantDetails activeSection="insurances" />} />
        </Route>
      </Routes>

      {!isFullData && <RequestFullDataModal isOpen={isFullModalOpen} onClose={() => setIsFullModalOpen(false)} onConfirm={handleFetchFull} isLoading={loading} tripName={tripData.name} />}
    </div>
  );
};

const TripWrapper = () => {
  const { guid } = useParams();
  return <TripProvider guid={guid}><InnerRoutes /></TripProvider>;
};

export default TripWrapper;
